import { useState, useEffect, useCallback } from 'react';
import { getAccounts, fetchTags } from '../api/endpointsBackend';
import { usePreferences } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  mayBeOnboarded, holdsInOnboarding, invitedWorkspace, onboardingVariant,
} from '../utils/onboardingGate';

// MI-297: derive onboarding progress from real data (+ acknowledgment flags),
// so the checklist self-corrects no matter how the user did each thing.
//
// These are the *setup* steps. Creating an account is the only essential; the
// rest are optional and satisfied by data OR by acknowledging/skipping them, so
// a user happy with the base setup still reaches "all done" and the checklist
// retires. Getting data in is deliberately NOT a step: the completion
// celebration and the per-page empty states own that call-to-action instead.

// Step order matches the wizard. Regional steps (language/country/currency) are
// wizard-only (defaults always applied) and not tracked here.
export const ONBOARDING_STEPS = [
  { key: 'account', optional: false },
  { key: 'categories', optional: true },
  { key: 'tag', optional: true },
];

function deriveSteps(data, onboarding) {
  const ob = onboarding || {};
  const doneByKey = {
    // Onboarding creates a cash account of its own once the country step
    // knows the currency (stamping cashAccountAt), and that one must not
    // stand in for the account the user came here to add, so for them the
    // step records its own save. Anyone onboarding did not seed — every user
    // from before this flow — has only real accounts, and those count:
    // otherwise the phone's Home offered them "finish setup", into a page with
    // no way out.
    account: !!ob.accountCreatedAt || (!ob.cashAccountAt && data.accounts > 0),
    // No reliable "user-created" flag on categories (MI-267) → ack-only.
    categories: !!ob.categoriesAckAt,
    tag: data.tags > 0 || !!ob.tagAckAt,
  };
  return ONBOARDING_STEPS.map((s) => ({ ...s, done: !!doneByKey[s.key] }));
}

export default function useOnboarding() {
  const { isAuthenticated } = useAuth();
  const { onboarding } = usePreferences();
  const { workspaces, current: currentWorkspace, ready: workspacesReady } = useWorkspace();
  const [data, setData] = useState({ accounts: 0, tags: 0, loading: true });

  const refresh = useCallback(async () => {
    try {
      // Just what the derived steps need: account (essential) + tag. Plain GETs;
      // the accounts are the list the whole app shares.
      const [accounts, tags] = await Promise.all([getAccounts(), fetchTags()]);
      setData({
        accounts: accounts?.length || 0,
        tags: tags?.length || 0,
        loading: false,
      });
    } catch (e) {
      setData((d) => ({ ...d, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) refresh();
  }, [isAuthenticated, refresh]);

  const steps = deriveSteps(data, onboarding);
  const doneCount = steps.filter((s) => s.done).length;
  const totalCount = steps.length;
  // Setup is done when every tracked step is (account + the optionals, each
  // satisfied by data or acknowledged). This is what the completion celebration
  // fires on; getting data in is promoted afterwards, not gated here.
  const allDone = doneCount === totalCount;
  const firstPending = steps.find((s) => !s.done);

  const checklistDismissed = !!onboarding?.checklistDismissedAt;
  const tourSeen = !!onboarding?.tourSeenAt;
  const ready = isAuthenticated && !data.loading; // only decide once data loaded

  // Resume model (MI-297 rev 2026-07-09). Distinguish *started* / *completed* /
  // *dismissed* so a user who leaves mid-setup is returned to the step they left
  // on, without ever re-onboarding an existing user.
  const started = !!onboarding?.wizardStartedAt;
  const completed = !!onboarding?.wizardCompletedAt;
  // No longer written — onboarding blocks, so nothing dismisses it. Still read,
  // so anyone who dismissed the old modal keeps falling through.
  const dismissed = !!onboarding?.wizardDismissedAt;
  // Users onboarded before the resume revision carry the legacy `wizardSeenAt`
  // and never got `wizardStartedAt`. Nothing here needs to name them: they have
  // no cohort marker, so they are never held, and `started` already keeps them
  // out of the checklist and the tour.

  // Who onboarding may hold. The marker is written once, by the backend, at
  // registration — so this can only ever be true for an account created after
  // it shipped. Deriving it from data instead (no accounts yet) would break the
  // moment onboarding seeds a cash account, and worse, a misread would hold a
  // real user away from data they already have.
  // The whole gate — marker plus a space of their own — lives in one pure
  // place, because it is the rule that decides whether someone can reach their
  // own data. See utils/onboardingGate.js.
  const mayHold = workspacesReady
    && mayBeOnboarded({ onboarding, workspaces, currentWorkspace });

  const held = holdsInOnboarding({ mayHold, completed, dismissed });
  const brandNew = held && !started;
  // Not "until every tracked step is done": those are all done by the time
  // the last step shows, and a reload there used to walk out of onboarding
  // with no movement yet. Held until the last step marks it finished.
  const resuming = held && started;
  // The checklist, the tour and the phone's setup chip belong to the same people
  // onboarding may hold, and to nobody else. `started` alone is not that: users
  // of the MI-297 wizard carry it too, and every one of these nudges leads into
  // /onboarding — which seeds a cash account into a workspace that already has
  // real ones, after which the account step reads as never done and the
  // checklist asks them to "create your first account" for good.
  const leftWizard = mayHold && started && (completed || dismissed);

  // Invited members get an onboarding of their own (the "guest" variant),
  // decided by the same pure module. `variant` is the one answer the layout's
  // redirect needs: 'owner' exactly when `held` above is true and no live
  // invitation applies, 'guest' for a first-run user the backend marked when
  // they accepted, null otherwise. Both wait for the workspace list — the
  // guest marker only counts while the invited workspace is still in it.
  const variant = workspacesReady
    ? onboardingVariant({ onboarding, workspaces, currentWorkspace })
    : null;
  const invited = workspacesReady ? invitedWorkspace({ onboarding, workspaces }) : null;
  // A guest stays one after finishing their flow: the owner's checklist and
  // tour are about setting up a space of your own, and must never reach them.
  // `mayHold` already excludes them (the invited membership makes
  // `ownsTheirSpace` false); this says so outright rather than lean on it.
  const guest = !!invited;

  // Coach-mark tour: once, right after the user finishes the wizard. It spotlights
  // sidebar nav (which exists with or without data), and the "source files" step
  // doubles as the "add your first statement" nudge — so a fresh, empty app is
  // exactly the right moment, not a reason to withhold it.
  const showTour = ready && mayHold && !guest && started && completed && !tourSeen;

  // The guest's tour: once, after they leave their flow, in the workspace they
  // were invited to. The owner tour's rule with the invite keys; the layout
  // still checks the path (/movements, computer only), as it does for the
  // owner's. Its own "seen" key, so neither tour can use up the other.
  const showGuestTour = ready && guest
    && !!onboarding?.inviteCompletedAt && !onboarding?.inviteTourSeenAt
    && currentWorkspace?.id === invited.id;

  return {
    loading: data.loading,
    data,
    steps,
    doneCount,
    totalCount,
    allDone,
    firstPendingKey: firstPending?.key || null,
    // The step the user was last on, when there is one. Resuming on the first
    // *pending* step can't express the regional steps, which aren't tracked.
    currentStepKey: onboarding?.currentStepKey || null,
    // Brand-new users start at step ①; resumers open where they left off.
    brandNew,
    resuming,
    // Onboarding is the only thing these users can do until they finish it, so
    // this doubles as the guard every app route is held behind — for either
    // variant: /onboarding picks its steps from `variant`. It does not wait
    // for the account and tag counts: `variant` reads only the preferences and
    // the workspace list, both settled before the layout mounts, and waiting
    // painted the app (Movements) for as long as that fetch took before the
    // redirect.
    showWizard: isAuthenticated && variant !== null,
    // Which flow /onboarding shows: 'owner' | 'guest' | null (not held).
    variant,
    // The checklist takes over once the user has left the wizard (completed,
    // dismissed, or legacy) with optional steps still pending. Owners only.
    showChecklist: ready && leftWizard && !guest && !allDone && !checklistDismissed,
    showTour,
    showGuestTour,
    // The invitation the guest flow is about, while it still applies (null
    // otherwise). The workspace row carries the host's name and photo
    // (`owner_first_name`, `owner_picture`, `name`) for the flow's header.
    invitedWorkspace: invited,
    inviteWorkspaceId: invited?.id ?? null,
    // The role held there now, from the listing; the marker's copy is only
    // what it was at accept time, and the host may have changed it since.
    inviteRole: invited ? invited.workspace_role || onboarding.inviteRole : null,
    // The guest flow's own resume key, never the owner's `currentStepKey`, so
    // the two flows cannot resume each other.
    inviteStepKey: onboarding?.inviteStepKey || null,
    // Whether any of onboarding applies to this user at all — for nudges drawn
    // outside this hook, like the phone's setup chip.
    mayHold,
    refresh,
  };
}
